/**
 * Date comparison utilities for the Monarch Dashboard.
 *
 * PoP (Period-over-Period): the immediately preceding period of equal length.
 * YoY (Year-over-Year): same period one year back, aligned to match the
 *   day-of-week of the start date (avoids comparing Thu to Sat).
 */

export interface ComparisonPeriod {
  start: Date;
  end: Date;
}

export interface ComparisonResult {
  pop: ComparisonPeriod;
  yoy: ComparisonPeriod;
}

/**
 * Given a date one year back, find the closest date that falls on the same
 * day of the week as the target. Shifts by at most +/- 3 days.
 */
function alignToWeekday(candidate: Date, targetDow: number): Date {
  const candidateDow = candidate.getDay();
  let diff = targetDow - candidateDow;
  if (diff > 3) diff -= 7;
  if (diff < -3) diff += 7;
  const aligned = new Date(candidate);
  aligned.setDate(aligned.getDate() + diff);
  return aligned;
}

export function computeComparisonPeriods(start: Date, end: Date): ComparisonResult {
  const durationMs = end.getTime() - start.getTime();
  const durationDays = Math.round(durationMs / (24 * 60 * 60 * 1000));

  // PoP: shift back by the same number of days
  const popEnd = new Date(start);
  popEnd.setDate(popEnd.getDate() - 1);
  const popStart = new Date(popEnd);
  popStart.setDate(popStart.getDate() - durationDays + 1);

  // YoY: go back one year, then align to same day-of-week
  const targetDow = start.getDay();
  const rawYoyStart = new Date(start);
  rawYoyStart.setFullYear(rawYoyStart.getFullYear() - 1);
  const yoyStart = alignToWeekday(rawYoyStart, targetDow);
  const yoyEnd = new Date(yoyStart);
  yoyEnd.setDate(yoyEnd.getDate() + durationDays - 1);

  return {
    pop: { start: popStart, end: popEnd },
    yoy: { start: yoyStart, end: yoyEnd },
  };
}

/** Format a date range for display: "Feb 27 -- Mar 26, 2025" */
export function formatDateRange(start: Date, end: Date): string {
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} \u2013 ${endStr}`;
}
